import {PageHeading} from "@ui-components/Container";
import { useIntl } from "react-intl";
import {useState, useEffect} from "react";
import {checkUploadedImage, uploadImage} from "@components/WorksiteImageUploader";
import Button from "@ui-components/Button";
import {PencilIcon} from "@heroicons/react/24/outline";
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";
import {
  CROP_ASPECT_RATIO,
  CROP_ASPECT_RATIO_TOLERANCE,
  PROFILE_LOGO_ASPECT_RATIO,
  PROFILE_LOGO_ASPECT_RATIO_TOLERANCE
} from "../../utils/constants";
import PrivateImage from "@components/PrivateImage";
import {getImageSize} from "@components/WorksiteImageUploader";
import {TrashIcon, ScissorsIcon, ExclamationTriangleIcon, ArrowUpTrayIcon} from "@heroicons/react/24/outline";
import ImageCropper from "../../components/ImageCropper";
import mime from "mime-types";
import authenticationService from "@services/AuthenticationService";
import apiRequest from "../../services/apiRequest";
import Spinner from "@ui-components/Spinner";
import {useLogo} from "./hooks/useLogo";
import { deleteFileFromS3 } from "pages/WorksitesDocuments/utils/FilesAndS3Utils";
import {LogoSchema} from "./types";

const re = /(?:\.([^.]+))?$/;
const maxSize = 5120
export const ProfileLogoEditor = () => {
  const countryApiRequest = useCountryAxiosInstance();
  const intl = useIntl()
  const [loading, setLoading] = useState(false)
  const userData = authenticationService.getUserData()
  // Image state

  const [signedUrl, setSignedUrl] = useState<string | undefined>()
  const [imageBeingUploadedProgress, setImageBeingUploadedProgress] = useState<{file:File, progress:number} | undefined >()
  const [nonCompliant, setNonCompliant] = useState(false)
  const [cropUploadProgresses, setCropUploadProgresses] = useState<number>(0)
  const [crop, setCrop] = useState<any>()
  const [showCrop, setShowCrop] = useState(false)
  const [logo, setLogo] = useState<LogoSchema | undefined>()

  const {
    loading: loadingLogo,
    getLogo,
    updateLogo,
    deleteLogo,
  } = useLogo();

  const fetchLogo = async () => {
    const logo: LogoSchema = await getLogo()
    setLogo(logo as LogoSchema)
  }

  useEffect(()=>{
    fetchLogo()
  },[])

  /*
  using account_user_id if it exists (otherwise user id)
  so that account user and contact user
  upload logos to same s3 bucket and directory.
   */
  const profileId = userData?.account_user_id || userData.id
  useEffect(() => {
    const fileInput = document.getElementById("logo-image-input") as HTMLInputElement
    if (fileInput) {
      fileInput.addEventListener("click", () => {
        fileInput.value = ""
      })
    }
  }, []);

  // Only certain roles can have a logo
  if(![
      'resale',
      'resale_tintometer',
      'contact_user',
      'enterprise',
      'design_studio',
      'freelance',
    ].includes(userData.role_type)){
    return null
  }

  // if loading, show spinner :)
  if (loading) {
    return (
      <>
        <PageHeading
          title={intl.formatMessage({id: "logo", defaultMessage: "Logo"})}
        />
        <div className={"flex w-full flex-col h-full items-center justify-center"}>
          <Spinner styleType={"white"}/>
        </div>
      </>
    )
  }

  // otherwise, show the logo editor component :)
  return <div className={"my-5"}>
    <PageHeading
      title={intl.formatMessage({id: "logo", defaultMessage: "Logo"})}
    />
    <div className="flex w-full flex-col h-full items-center justify-center">
      <div className="w-full mb-4 flex justify-center">
        <input
          type="file"
          accept="image/jpeg,image/png"
          style={{display: "none"}}
          id={"logo-image-input"}
          onChange={async (e) => {
            const prevLogo = {...logo};
            const file = e.target.files?.[0];
            if (!file) return;
            const {format, error} = checkUploadedImage(file, maxSize);

            let {image_url, des_image, loadedOn} = await uploadImage(
              file,
              format,
              () => {
                setLoading(true);
                setImageBeingUploadedProgress({file, progress: 0});
              },
              (progressEvent) => {
                const progress = Math.floor(
                  (progressEvent.loaded * 100) / progressEvent.total
                );

                setImageBeingUploadedProgress({file, progress});
              },
              () => {
                setLoading(false);
                setImageBeingUploadedProgress({file, progress: 100});
              },
              null,
              countryApiRequest,
              `images/it/profile_logos/${profileId}`,
              "profile_logos"
            );
            setImageBeingUploadedProgress(undefined);
            await updateLogo({
              url: image_url,
              filename: file.name,
              loaded_on: loadedOn,
            });
            await fetchLogo()
            if (prevLogo.url) {
              await deleteFileFromS3(
                countryApiRequest,
                `images/${userData.countries}/profile_logos/${userData.id}`,
                "profile_logos",
                prevLogo.url,
                true
              );
            }
          }}
        />
        <div className={"flex flex-col items-center w-full"}>
          <div className="flex flex-row w-full gap-4">
            <div>
              <div className="flex flex-col gap-2 mb-1">
                {!crop && !showCrop && (
                  <Button
                    disabled={
                      !!imageBeingUploadedProgress &&
                      imageBeingUploadedProgress.progress < 100
                    }
                    submitting={loading}
                    onClick={() => {
                      document.getElementById("logo-image-input")?.click();
                    }}
                  >
                    {!logo?.url ? (
                      <ArrowUpTrayIcon className="h-5 w-5 mr-2"/>
                    ) : (
                      <PencilIcon className="h-5 w-5 mr-2"/>
                    )}
                    {!logo?.url
                      ? intl.formatMessage({
                        id: "upload_logo",
                        defaultMessage: "Carica logo",
                      })
                      : intl.formatMessage({
                        id: "edit_logo",
                        defaultMessage: "Modifica logo",
                      })}
                  </Button>
                )}
                {logo?.url && !imageBeingUploadedProgress && !crop && !showCrop && (
                  <>
                    <Button
                      full
                      styleType="default"
                      onClick={() => {
                        setShowCrop(true);
                      }}
                      disabled={!signedUrl}
                    >
                      <ScissorsIcon className="w-4"/>
                      {intl.formatMessage({id: "crop"})}
                    </Button>
                    <Button
                      full
                      styleType="danger"
                      onClick={async () => {
                        const prevLogo = {...logo};
                        await deleteLogo();
                        setLogo(undefined)
                        await deleteFileFromS3(
                          countryApiRequest,
                          `images/${userData.countries}/profile_logos/${userData.id}`,
                          "profile_logos",
                          prevLogo.url,
                          true
                        );
                        setSignedUrl(undefined);
                        setNonCompliant(false);
                      }}
                    >
                      <TrashIcon className="w-4"/>
                      {intl.formatMessage({id: "delete"})}
                    </Button>
                  </>
                )}
              </div>
            </div>
            {logo?.url && !imageBeingUploadedProgress && !crop && !showCrop && (
              <div className="w-2/5">
                {/* ==================== Image ==================== */}
                <div
                  className={`flex flex-col items-center gap-4 ${
                    crop ? "opacity-30 pointer-events-none" : ""
                  }`}
                >
                  <div
                    className={`border-4 overflow-hidden`}
                    style={{
                      // aspectRatio: CROP_ASPECT_RATIO.toString(),
                      objectFit: "contain",
                    }}
                  >
                    <PrivateImage
                      image_url={logo.url}
                      tag="img"
                      className="w-full h-full bg-cover bg-center"
                      // aspectRatio={PROFILE_LOGO_ASPECT_RATIO}
                      fixedAspectRatio={false}
                      onRetrievedSignedURL={(sUrl: string) => {
                        // collect the signed url into the state
                        setSignedUrl(sUrl);
                        // mark image as non-compliant if its aspect ratio is different from the preferred
                        getImageSize(sUrl, (w: number, h: number) => {
                          if (
                            Math.abs(w / h - PROFILE_LOGO_ASPECT_RATIO) <
                            PROFILE_LOGO_ASPECT_RATIO_TOLERANCE
                          )
                            return;
                          setNonCompliant(true);
                        });
                      }}
                      {...{loadedOn: logo.loaded_on}}
                    />
                  </div>
                  <div className="flex-grow">
                    {/* -------------------- Controls -------------------- */}
                    {/*{nonCompliant && (*/}
                    {/*  <div className="flex items-center gap-1 text-xs text-yellow-500 my-1.5">*/}
                    {/*    <ExclamationTriangleIcon className="w-3"/>*/}
                    {/*    <span>*/}
                    {/*        {intl.formatMessage({*/}
                    {/*          id: "non_standard_image_size",*/}
                    {/*        })}*/}
                    {/*      </span>*/}
                    {/*  </div>*/}
                    {/*)}*/}
                  </div>
                </div>
              </div>
            )}
          </div>
          {showCrop && logo?.url && (
            <div className="mt-1">
              <div className="w-full pl-16">
                <svg
                  width="15"
                  height="15"
                  className="fill-current text-gray-100"
                >
                  <polygon points="7.5 2.25, 15 15, 0 15"/>
                </svg>
              </div>
              <div className="py-3 px-4 bg-gray-100 border-4 border-t-0 rounded-2xl">
                <p className="mb-2 flex gap-2 items-center text-base">
                  <ScissorsIcon className="w-4"/>
                  <span className="font-bold">
                      {intl.formatMessage({id: "crop_image"})}
                    </span>
                </p>
                <ImageCropper
                  intl={intl}
                  originalImage={signedUrl}
                  mimeType={mime.lookup(logo.url)}
                  cropState={crop}
                  onCropChange={(crop: any) => {
                    setCrop(crop);
                  }}
                  // aspectRatio={CROP_ASPECT_RATIO}
                  imageDesc={logo.filename}
                  onConfirm={async (file: File) => {
                    if (!logo) return;
                    const prevLogo = {...logo};
                    setLoading(true);
                    setNonCompliant(false);
                    setCrop(undefined);
                    setShowCrop(false);
                    const {image_url, des_image, loadedOn} = await uploadImage(
                      file,
                      re.exec(prevLogo.url).slice(-1)[0],
                      () => {
                        setCropUploadProgresses(0);
                      },
                      (progressEvent) => {
                        const percentCompleted = Math.floor(
                          (progressEvent.loaded * 100) / progressEvent.total
                        );
                        setCropUploadProgresses(percentCompleted);
                      },
                      () => {
                        // remove current image url from crops in 'sending' state
                        setCropUploadProgresses(100);
                      },
                      null,//prevLogo.url,
                      countryApiRequest,
                      `images/it/profile_logos/${profileId}`,
                      "profile_logos"
                    );
                    await updateLogo({
                      url: image_url,
                      filename: file.name,
                      loaded_on: loadedOn,
                    })
                    setLogo(
                      {
                        url: image_url,
                        filename: file.name,
                        loaded_on: loadedOn,
                      } as LogoSchema
                    )
                    await deleteFileFromS3(
                      countryApiRequest,
                      `images/${userData.countries}/profile_logos/${userData.id}`,
                      "profile_logos",
                      prevLogo.url,
                      true
                    );
                    setLoading(false);
                  }}
                  onCancel={() => {
                    setCrop(undefined);
                    setShowCrop(false);
                  }}
                  submitting={loading}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
}